import { useState } from "react";
import { Link, useLocation } from "wouter";
import { useCreateJob, JobInputUrgency } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { AYRSHIRE_TOWNS } from "@/hooks/use-town";
import { useAnalytics } from "@/hooks/use-analytics";

const TRADE_TYPES = [
  "Plumber", "Electrician", "Joiner", "Builder", "Roofer",
  "Painter & Decorator", "Gardener", "Landscaper", "Cleaner", "Handyman",
  "Tiler", "Plasterer", "Heating Engineer", "Window Cleaner", "Locksmith",
];

const WHEN_OPTIONS = [
  { label: "🚨 It's urgent!", value: "emergency", colour: "border-red-400 bg-red-50 text-red-700" },
  { label: "⏰ Within 24 hrs", value: "within_24h", colour: "border-orange-400 bg-orange-50 text-orange-700" },
  { label: "📅 This week", value: "this_week", colour: "border-blue-400 bg-blue-50 dark:bg-blue-900/20 text-blue-700" },
  { label: "📆 This month", value: "this_month", colour: "border-green-400 bg-green-50 text-green-700" },
  { label: "🔍 Just browsing", value: "flexible", colour: "border-slate-300 bg-slate-50 text-slate-600" },
];

const BUDGET_OPTIONS = [
  "£0–£50", "£50–£150", "£150–£250",
  "£250–£500", "£500–£1,000", "£1,000+", "Not sure yet",
];

const TOWNS = AYRSHIRE_TOWNS.filter(t => t !== "All Towns");

export default function PostJob() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const { mutateAsync: createJob, isPending } = useCreateJob();
  const { track } = useAnalytics();

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(TRADE_TYPES[0]!);
  const [description, setDescription] = useState("");
  const [town, setTown] = useState("Ayr");
  const [urgency, setUrgency] = useState("flexible");
  const [budget, setBudget] = useState(BUDGET_OPTIONS[0]!);
  const [contactName, setContactName] = useState(user?.name ?? "");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState(user?.email ?? "");

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 max-w-md w-full text-center">
          <div className="text-5xl mb-4">🔧</div>
          <h1 className="text-xl font-bold text-slate-800 mb-2">Sign in to get quotes</h1>
          <p className="text-slate-500 text-sm mb-6">Create a free account and tradespeople in your area will be in touch within hours.</p>
          <div className="space-y-2 text-sm text-slate-600 mb-6 text-left bg-primary/5 rounded-xl p-4">
            {["Describe what you need — takes 2 minutes", "Qualified local tradespeople get notified instantly", "Your contact details stay hidden until they unlock your request", "Completely free for residents — no hidden charges"].map(b => (
              <div key={b} className="flex items-center gap-2"><span className="text-emerald-500">✓</span>{b}</div>
            ))}
          </div>
          <div className="flex gap-3">
            <Link href="/login" className="flex-1"><Button variant="outline" className="w-full">Login</Button></Link>
            <Link href="/signup" className="flex-1"><Button className="w-full bg-primary hover:bg-primary/90">Create Account</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !description || !contactName) {
      toast({ title: "Missing details", description: "Please fill in the title, description, and your name.", variant: "destructive" });
      return;
    }
    const digits = contactPhone.replace(/\D/g, "");
    const national = digits.startsWith("44") ? `0${digits.slice(2)}` : digits;
    if (!/^07\d{9}$/.test(national)) {
      toast({ title: "Valid mobile number required", description: "Tradespeople pay to unlock your request and need a working UK mobile number.", variant: "destructive" });
      return;
    }
    try {
      await createJob({ data: { title, category, description, town, urgency: urgency as JobInputUrgency, budgetRange: budget, contactName, contactPhone, contactEmail } });
      track("job_posted", { screen: "post-job", metadata: { category, town, urgency } });
      toast({ title: "Request sent!", description: `Local ${category.toLowerCase()}s in ${town} have been notified.` });
      setLocation("/jobs");
    } catch {
      toast({ title: "Something went wrong", description: "Couldn't send your request. Please try again.", variant: "destructive" });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        <Link href="/jobs" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-primary mb-6 transition-colors">
          ← Back to Quotes
        </Link>

        <div className="bg-primary rounded-2xl p-5 mb-4 flex items-center gap-4">
          <span className="text-3xl">🔧</span>
          <div>
            <h1 className="text-xl font-bold text-white">What do you need doing?</h1>
            <p className="text-white/70 text-sm">Requesting as {user.name}</p>
          </div>
        </div>

        <div className="bg-primary/5 border border-primary/15 rounded-xl p-3 mb-4 flex items-start gap-2 text-sm text-primary">
          🔒 Your contact details are kept private. Tradespeople only see them after paying to respond to your request.
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Section title="What needs doing?">
            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Short summary *</label>
                <Input required placeholder='e.g. "Need a painter for my living room"' value={title} onChange={e => setTitle(e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Tell us more *</label>
                <Textarea required rows={4} placeholder="Describe the work — size, access, special requirements..." value={description} onChange={e => setDescription(e.target.value)} />
              </div>
            </div>
          </Section>

          <Section title="Type of tradesperson">
            <div className="p-3 flex flex-wrap gap-2">
              {TRADE_TYPES.map(cat => (
                <button key={cat} type="button" onClick={() => setCategory(cat)}
                  className={`px-3 py-1.5 rounded-full border text-sm font-medium transition-all ${category === cat ? "bg-primary text-white border-primary" : "border-slate-200 text-slate-600 hover:border-primary hover:text-primary"}`}>
                  {cat}
                </button>
              ))}
            </div>
          </Section>

          <Section title="Where are you based?">
            <div className="p-3 flex flex-wrap gap-2">
              {TOWNS.map(t => (
                <button key={t} type="button" onClick={() => setTown(t)}
                  className={`px-3 py-1.5 rounded-full border text-sm font-medium transition-all ${town === t ? "bg-primary text-white border-primary" : "border-slate-200 text-slate-600 hover:border-primary hover:text-primary"}`}>
                  {t}
                </button>
              ))}
            </div>
          </Section>

          <Section title="When do you need it done?">
            <div className="p-3 flex flex-wrap gap-2">
              {WHEN_OPTIONS.map(opt => (
                <button key={opt.value} type="button" onClick={() => setUrgency(opt.value)}
                  className={`px-4 py-2 rounded-full border text-sm font-medium transition-all ${urgency === opt.value ? opt.colour + " border-opacity-100 font-bold" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </Section>

          <Section title="Rough budget in mind?">
            <div className="p-3 flex flex-wrap gap-2">
              {BUDGET_OPTIONS.map(b => (
                <button key={b} type="button" onClick={() => setBudget(b)}
                  className={`px-3 py-1.5 rounded-full border text-sm font-medium transition-all ${budget === b ? "bg-secondary/20 text-amber-700 border-secondary font-bold" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                  {b}
                </button>
              ))}
            </div>
          </Section>

          <Section title="Your contact details">
            <div className="p-4 space-y-3">
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-lg p-2.5 text-xs text-amber-700 flex items-center gap-2">
                🔒 These stay private — tradespeople only see them after paying to contact you.
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Your name *</label>
                <Input required placeholder="John Smith" value={contactName} onChange={e => setContactName(e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Mobile number *</label>
                <Input type="tel" required placeholder="07700 900 000" value={contactPhone} onChange={e => setContactPhone(e.target.value)} />
                <p className="text-xs text-slate-400 mt-1">Required because tradespeople pay to unlock your contact details.</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Email address</label>
                <Input type="email" placeholder="you@example.com" value={contactEmail} onChange={e => setContactEmail(e.target.value)} />
              </div>
            </div>
          </Section>

          <Button type="submit" className="w-full bg-primary hover:bg-primary/90 font-bold text-base py-6" disabled={isPending}>
            {isPending ? "Sending…" : "🔍 Find Tradespeople"}
          </Button>
          <p className="text-xs text-center text-slate-400 pb-4">
            Your request goes to all {category.toLowerCase()}s registered across Ayrshire. Expect to hear back within a few hours.
          </p>
        </form>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-sm font-bold text-slate-700 mb-1.5 px-1">{title}</h3>
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">{children}</div>
    </div>
  );
}
